// src/pwa/src/views/FinanceView/api/useFinanceStore.ts
// Zustand store wiring for the Finance View. Adds Issue 74 catalog sync hook
// (with IndexedDB fallback) so the "+ Add from Catalog" dropdown never
// silently fails empty.

import { create } from 'zustand';
import { syncManager } from '../../../services/sync';
import { db } from '../../../db';
import {
    financeApi,
    getProducts,
    FinancialData,
    ExpenseItem,
    ReconciliationItem,
    BudgetVariance,
    BudgetEntry,
    Quote,
    Invoice,
    Payment,
    FinancialReport,
    PeriodType,
    CustomPeriod,
    Product,
    FinanceLineItem,
} from './financeApi';
// Re-export the item shape so existing callers can import it from the store too.
export type { FinanceLineItem };

// ─── FinanceState type ───────────────────────────────────────────────────

export interface FinanceState {
    data: FinancialData | null;
    quotes: Quote[];
    invoices: Invoice[];
    expenses: ExpenseItem[];
    payments: Payment[];
    reconciliation: ReconciliationItem[];
    variances: BudgetVariance[];
    budgets: BudgetEntry[];
    products: Product[];
    period: PeriodType;
    customPeriod: CustomPeriod | null;
    isLoading: boolean;
    error: string | null;
    budgetsError: string | null;
    catalogError: string | null;
    catalogSyncing: boolean;

    setPeriod: (period: PeriodType, customPeriod?: CustomPeriod | null) => Promise<void>;
    refreshData: () => Promise<void>;
    loadProducts: () => Promise<void>;
    syncCatalog: () => Promise<void>;
    addBudget: (entry: Partial<BudgetEntry> & { category: string; budgeted: number; period: string }) => Promise<void>;
    addQuote: (quote: Partial<Quote>) => Promise<void>;
    updateQuoteStatus: (id: string, status: Quote['status']) => Promise<void>;
    updateQuote: (id: string, data: Partial<Quote>) => Promise<void>;
    deleteQuote: (id: string) => Promise<void>;
    createInvoice: (invoice: Partial<Invoice>) => Promise<void>;
    updateInvoiceStatus: (id: string, status: Invoice['status']) => Promise<void>;
    updateInvoice: (id: string, data: Partial<Invoice>) => Promise<void>;
    deleteInvoice: (id: string) => Promise<void>;
    convertQuoteToInvoice: (quoteId: string) => Promise<void>;
    sendReminder: (invoiceId: string) => Promise<void>;
    addExpense: (expense: Partial<ExpenseItem>) => Promise<void>;
    updateExpense: (id: string, data: Partial<ExpenseItem>) => Promise<void>;
    deleteExpense: (id: string) => Promise<void>;
    logPayment: (payment: Partial<Payment>) => Promise<void>;
    updatePayment: (id: string, data: Partial<Payment>) => Promise<void>;
    deletePayment: (id: string) => Promise<void>;
    linkPaymentToInvoice: (paymentId: string, invoiceId: string) => Promise<void>;
    resolveReconciliation: (id: string) => Promise<void>;
    deleteReconciliation: (id: string) => Promise<void>;
    generateReport: (type: FinancialReport['type'], period: string, customPeriod: CustomPeriod | null) => Promise<FinancialReport>;
    exportReport: (report: FinancialReport) => Promise<string>;
}

// ─── IndexedDB fallback for product catalog ──────────────────────────────

async function loadProductsFromCache(): Promise<Product[]> {
    try {
        const cached = await db.products.toArray();
        if (cached && cached.length > 0) {
            return cached.map((row: any) => ({
                id: row.sku || row.id || `prod-${Math.random().toString(36).slice(2)}`,
                name: row.name || row.Product_Name || 'Unnamed Product',
                category: row.category || row.Category || 'General',
                price: Number(row.price || row.Price_ZAR || row.Price || 0),
                billingFrequency: row.billingFrequency || row.Billing_Frequency || undefined,
                description: row.description || row.Description || undefined,
                deliverableType: row.deliverableType || row.Deliverable_Type || undefined,
                status: row.status || row.Status || undefined,
            }));
        }
    } catch {
        // IndexedDB may be unavailable in some environments
    }
    return [];
}

// ─── Store ──────────────────────────────────────────────────────────────

export const useFinanceStore = create<FinanceState>((set, get) => ({
    data: null,
    quotes: [],
    invoices: [],
    expenses: [],
    payments: [],
    reconciliation: [],
    variances: [],
    budgets: [],
    products: [],
    period: 'monthly' as PeriodType,
    customPeriod: null,
    isLoading: false,
    error: null,
    budgetsError: null,
    catalogError: null,
    catalogSyncing: false,

    setPeriod: async (period: PeriodType, customPeriod?: CustomPeriod | null) => {
        set({ isLoading: true, error: null, budgetsError: null });
        try {
            const periodStr = period as string;
            const effectiveCustomPeriod = customPeriod ?? get().customPeriod;
            const [data, expenses, payments, reconciliation, variances, quotes, invoices, budgetsResult] = await Promise.all([
                financeApi.getFinancialData(periodStr, effectiveCustomPeriod),
                financeApi.getExpenses(period, effectiveCustomPeriod),
                financeApi.getPayments(period, effectiveCustomPeriod),
                financeApi.getReconciliationData(period, effectiveCustomPeriod),
                financeApi.getBudgetVariances(period),
                financeApi.getQuotes(period, effectiveCustomPeriod).catch(() => []),
                financeApi.getInvoices(period, effectiveCustomPeriod).catch(() => []),
                financeApi.getBudgets(period).catch((e) => {
                    const msg = e instanceof Error ? e.message : 'Unable to fetch the Budgets tab from the server. The sync endpoint returned an internal error.';
                    set({ budgetsError: msg });
                    return [];
                }),
            ]);
            set({ data, expenses, payments, reconciliation, variances, quotes, invoices, budgets: budgetsResult, period, customPeriod: effectiveCustomPeriod, isLoading: false });
        } catch (e: unknown) {
            const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred';
            set({ error: errorMessage, isLoading: false });
        }
    },

    refreshData: async () => {
        const data = get().data;
        if (data) {
            await get().setPeriod(data.period);
        }
    },

    loadProducts: async () => {
        try {
            const fromServer = await getProducts();
            if (Array.isArray(fromServer) && fromServer.length > 0) {
                set({ products: fromServer, catalogError: null });
                return;
            }
        } catch {
            // network error — fall through to cache
        }

        // Fallback to local IndexedDB cache (already populated by CartContext or
        // syncManager.fetchBusinessModules on app boot).
        const fromCache = await loadProductsFromCache();
        if (fromCache.length > 0) {
            set({ products: fromCache, catalogError: null });
            return;
        }
        set({
            products: [],
            catalogError: 'Catalog not yet synced. Tap "Sync Catalog" to load Master_Products from the Hub sheet.',
        });
    },

    syncCatalog: async () => {
        set({ catalogSyncing: true, catalogError: null });
        try {
            await syncManager.fetchTabData('Master_Products', 'admin@chrysalinks.co.za');
            await get().loadProducts();
            const final = get().products;
            if (final.length === 0) {
                set({ catalogError: 'Master_Products sheet returned no rows. Add a product to the tab first.' });
            }
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Could not sync Master_Products from the Hub sheet.';
            set({ catalogError: msg });
        } finally {
            set({ catalogSyncing: false });
        }
    },

    addBudget: async (entry) => {
        const newEntry = await financeApi.addBudget(entry);
        set({ budgets: [...get().budgets, newEntry] });
    },

    addQuote: async (quote) => {
        const newQuote = await financeApi.createQuote(quote);
        set({ quotes: [...get().quotes, newQuote] });
    },

    updateQuoteStatus: async (id, status) => {
        await financeApi.updateQuoteStatus(id, status);
        set({ quotes: get().quotes.map(q => q.id === id ? { ...q, status } : q) });
    },
    updateQuote: async (id, data) => {
        await financeApi.updateQuote(id, data);
        set({ quotes: get().quotes.map(q => q.id === id ? { ...q, ...data } : q) });
    },
    deleteQuote: async (id) => {
        await financeApi.deleteQuote(id);
        set({ quotes: get().quotes.filter(q => q.id !== id) });
    },

    createInvoice: async (invoice) => {
        const newInvoice = await financeApi.createInvoice(invoice);
        set({ invoices: [...get().invoices, newInvoice] });
    },
    updateInvoiceStatus: async (id, status) => {
        await financeApi.updateInvoiceStatus(id, status);
        set({ invoices: get().invoices.map(inv => inv.id === id ? { ...inv, status } : inv) });
    },
    updateInvoice: async (id, data) => {
        await financeApi.updateInvoice(id, data);
        set({ invoices: get().invoices.map(inv => inv.id === id ? { ...inv, ...data } : inv) });
    },
    deleteInvoice: async (id) => {
        await financeApi.deleteInvoice(id);
        set({ invoices: get().invoices.filter(inv => inv.id !== id) });
    },

    convertQuoteToInvoice: async (quoteId) => {
        const invoice = await financeApi.convertQuoteToInvoice(quoteId);
        set({ invoices: [...get().invoices, invoice] });
        await financeApi.updateQuoteStatus(quoteId, 'invoiced');
        set({ quotes: get().quotes.map(q => q.id === quoteId ? { ...q, status: 'invoiced' as Quote['status'] } : q) });
    },

    sendReminder: async (invoiceId) => {
        await financeApi.sendPaymentReminder(invoiceId);
    },

    addExpense: async (expense) => {
        const newExpense = await financeApi.logExpense(expense);
        set({ expenses: [...get().expenses, newExpense] });
    },
    updateExpense: async (id, data) => {
        await financeApi.updateExpense(id, data);
        set({ expenses: get().expenses.map(e => e.id === id ? { ...e, ...data } : e) });
    },
    deleteExpense: async (id) => {
        await financeApi.deleteExpense(id);
        set({ expenses: get().expenses.filter(e => e.id !== id) });
    },

    logPayment: async (payment) => {
        const newPayment = await financeApi.logPayment(payment);
        set({ payments: [...get().payments, newPayment] });
    },
    updatePayment: async (id, data) => {
        await financeApi.updatePayment(id, data);
        set({ payments: get().payments.map(p => p.id === id ? { ...p, ...data } : p) });
    },
    deletePayment: async (id) => {
        await financeApi.deletePayment(id);
        set({ payments: get().payments.filter(p => p.id !== id) });
    },
    linkPaymentToInvoice: async (paymentId, invoiceId) => {
        await financeApi.linkPaymentToInvoice(paymentId, invoiceId);
        set({ payments: get().payments.map(p => p.id === paymentId ? { ...p, invoiceId } : p) });
    },

    resolveReconciliation: async (id) => {
        await financeApi.resolveConflict(id);
        set({ reconciliation: get().reconciliation.map(r => r.id === id ? { ...r, status: 'matched' as const } : r) });
    },
    deleteReconciliation: async (id) => {
        await financeApi.deleteReconciliation(id);
        set({ reconciliation: get().reconciliation.filter(r => r.id !== id) });
    },

    generateReport: async (type, period, customPeriod) => {
        return await financeApi.getFinancialReport(type, period, customPeriod ?? null);
    },

    exportReport: async (report) => {
        return await financeApi.exportReport(report);
    }
}));
